import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable'
import { IColumnVisibility } from 'components/DataTable/types'
import FbButton from 'components/FbUI/FbButton'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import ContactExportModal from 'components/Modals/ExportModal/ContactExportModal'
import dialogConfirm from 'components/dialogConfirm'
import { ContactResponse } from 'models/contacts'
import React, { useEffect, useMemo, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Ban, Mail, XCircle } from 'lucide-react'
import { BsDownload, BsTags } from 'react-icons/bs'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import styled from 'styled-components'
import { FilterIdentifier } from '../../../models/saved_view'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import {
  ActionsDropdown,
  createDeleteAction,
  createEditAction,
} from '../../Buttons/ActionsDropdown'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'
import { TableSearch } from '../../Filters/TableSearch'
import EditContactModal from '../../Modals/EditContactModal'
import * as S from '../CommonTable.styles'
import { BulkDeleteContactsModal } from 'components/Modals/BulkDeleteContactsModal/BulkDeleteContactsModal'
import ContactsFilterset from './ContactsFilterset'
import { useContactsTableColumns } from './useContactsTableColumns'
import { useBulkContactsDeleter } from './useBulkContactsDeleter'
import { SEQUENCE_SORTABLE_FIELDS } from './sequence/sequenceColumnInfo'
import SendEmailModal from 'components/Modals/SendEmailModal'
import { SiSalesforce } from 'react-icons/si'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { ContactCompany } from 'models/companies'
const tooltip = <Tooltip id="tooltip">Download limited to 10,000 rows</Tooltip>

type SortableFields = (keyof ContactResponse | string)[]

export type ContactsTableRef = {
  refetch: () => void
}

const BASE_SORTABLE_FIELDS: SortableFields = [
  'email',
  'id',
  'contact_company',
  'name',
  'title',
  'type',
  'url',
  'sales_stage',
  'account_owners',
  'contact_labels',
  'company_tags',
  'phone_number',
  'deal_count',
  'sales_stages',
  'company_size',
  'company_cuisine_50',
  'created',
  'modified',
  'is_do_not_contact',
  'date_last_sent',
  'date_last_received',
]

const PAGE_SIZE = 100

interface ContactsTableProps {
  setTotalRowsCount?: (count: number) => void
  handleContactUpdated?: () => void
  forCompany?: Partial<ContactCompany>
  disableUrlPagination?: boolean
  disableExportButton?: boolean
  tableKey: string
  filterIdentifierModifier?: string
  baseFilters?: Record<string, any>
  forDoorProxyId?: number
  campaignId?: number
}

function ContactsTableComponent(props: ContactsTableProps) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const IDENTIFIER = 'CONTACTS_TABLE' + (props.filterIdentifierModifier ?? '')
  const contactsFilterStore = filterStoreRepo.getStore(
    IDENTIFIER as FilterIdentifier
  )
  const { page, setPage } = contactsFilterStore()
  // Local-only — toggle resets on navigation, matching SamplesTable's
  // ``showInactive`` precedent. Promote to filterStore if persistence is needed.
  const [inSequenceOnly, setInSequenceOnly] = useState(false)

  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()

  const {
    state: { totalSelectedRows, rowSelection, isAllRowsSelected },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()
  const featureFlag = useFeatureFlagService()
  const campaignMode = !!props.campaignId && featureFlag.enableEmailSequencing
  const sortableFields = useMemo(
    () =>
      campaignMode
        ? [...BASE_SORTABLE_FIELDS, ...SEQUENCE_SORTABLE_FIELDS]
        : BASE_SORTABLE_FIELDS,
    [campaignMode]
  )
  const [showExportModal, setShowExportModal] = useState(false)
  const [showAssignContactTags, setShowAssignContactTags] = useState(false)
  const [showAssignCompanyTags, setShowAssignCompanyTags] = useState(false)
  const [showEditContactModal, setShowEditContactModal] =
    React.useState<ContactResponse | null>(null)
  const [showWriteEmailForContactModal, setShowWriteEmailForContactModal] =
    React.useState<ContactResponse | null>(null)

  const filtersAsParams = {
    ...useFilterParams(contactsFilterStore),
    ...props.baseFilters,
  }

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filtersAsParams,
      ...sortParams,
      company: props.forCompany?.id,
      chain: props.forCompany?.chain,
      door_proxy_id: props.forDoorProxyId,
      ...(campaignMode ? { include_sequence_data: 'true' } : {}),
      ...(campaignMode && inSequenceOnly ? { in_sequence: 'true' } : {}),
    }),
    [
      filtersAsParams,
      sortParams,
      props.forCompany,
      props.forDoorProxyId,
      campaignMode,
      inSequenceOnly,
    ]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
  })

  // DATA FETCHING
  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      clearSelectedRows()

      const body = {
        ...filterAndSortParams,
        page: page,
        limit: PAGE_SIZE,
      }

      return await api.getContactsList(body, signal)
    },
  })
  // END DATA FETCHING

  const bulkDelete = useBulkContactsDeleter({
    data: data?.results ?? [],
    tableQueryKey: TABLE_QUERY_KEY,
    filterParams: filterAndSortParams,
    forCompanyId: props.forCompany?.id,
    forDoorProxyId: props.forDoorProxyId,
    campaignId: props.campaignId,
  })

  const columns = [
    ...(useContactsTableColumns(campaignMode) as any),
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      meta: {
        isEditable: false,
        headerDisableOrdering: true,
        isLastColumn: true,
        rightAlign: true,
      },
      cell: (info) => {
        const contact = info.row.original
        const contactId = contact.id
        const name = `${contact.first_name} ${contact.last_name}`

        return (
          <ActionsDropdown
            actions={[
              createEditAction(() => setShowEditContactModal(contact)),
              {
                label: 'Send Email',
                icon: Mail,
                onClick: () => setShowWriteEmailForContactModal(contact),
              },
              ...(featureFlag.enableEmailSequencing
                ? [
                    {
                      label: contact.is_do_not_contact
                        ? 'Remove from Do Not Contact'
                        : 'Mark as Do Not Contact',
                      icon: Ban,
                      onClick: () =>
                        handleContactDoNotContactToggle(
                          contactId,
                          name,
                          contact.is_do_not_contact
                        ),
                      hasSeparatorAfter: true,
                    },
                  ]
                : []),
              createDeleteAction(() => handleContactDelete(contactId, name)),
            ]}
          />
        )
      },
    },
  ] satisfies ColumnDef<ContactResponse, unknown>[]

  const handleContactDoNotContactToggle = async (
    contactId: number,
    name: string,
    isCurrentlyDnc: boolean
  ) => {
    const target = !isCurrentlyDnc
    if (target) {
      const confirmed = await dialogConfirm(
        <>
          Are you sure you want to mark <b>{name}</b> as Do Not Contact? This
          contact will be excluded from all campaign sequences.
        </>,
        'Mark as Do Not Contact?',
        '',
        { confirmLabel: 'Mark as Do Not Contact' }
      )
      if (!confirmed) return
    }
    const [err] = await to(
      api.bulkSetContactsDoNotContact({
        include_ids: [contactId],
        is_do_not_contact: target,
      })
    )
    if (err) {
      toast.error('Failed to update Do Not Contact status')
      return
    }
    toast.success(
      target
        ? `${name} marked as Do Not Contact`
        : `${name} removed from Do Not Contact list`
    )
    queryClient.invalidateQueries({
      queryKey: TABLE_QUERY_KEY,
      exact: false,
    })
  }

  const handleBulkMarkDoNotContact = async () => {
    const confirmed = await dialogConfirm(
      <>
        Are you sure you want to mark <b>{totalSelectedRows}</b> contact
        {totalSelectedRows === 1 ? '' : 's'} as Do Not Contact? They will be
        excluded from all campaign sequences.
      </>,
      'Mark as Do Not Contact?',
      '',
      { confirmLabel: 'Mark as Do Not Contact' }
    )
    if (!confirmed) return

    const [err, res] = await to(
      api.bulkSetContactsDoNotContact({
        include_ids: isAllRowsSelected
          ? []
          : (data?.results ?? [])
              .filter((_, i) => rowSelection[i])
              .map((r) => r.id),
        exclude_ids: isAllRowsSelected
          ? (data?.results ?? [])
              .filter((_, i) => !rowSelection[i])
              .map((r) => r.id)
          : [],
        filter_params: filterAndSortParams,
        is_do_not_contact: true,
      })
    )
    if (err) {
      toast.error('Failed to mark contacts as Do Not Contact')
      return
    }
    toast.success(res?.message ?? 'Selected contacts marked as Do Not Contact')
    clearSelectedRows()
    queryClient.invalidateQueries({
      queryKey: TABLE_QUERY_KEY,
      exact: false,
    })
  }

  const handleContactDelete = async (contactId: number, name: string) => {
    if (
      await dialogConfirm(
        <>
          Are you sure you want to delete <b>{name}</b>? This action cannot be
          undone.
        </>,
        'Delete Contact?',
        '',
        { confirmLabel: 'Delete' }
      )
    ) {
      const [err] = await to(api.deleteContact(contactId))
      if (!err) {
        toast.success('Contact deleted successfully')
        refetch()
        if (props.forCompany?.id) {
          queryClient.invalidateQueries({
            queryKey: ['company', props.forCompany.id],
          })
        }
        if (props.forDoorProxyId) {
          queryClient.invalidateQueries({
            queryKey: ['door_proxy', props.forDoorProxyId],
          })
        }
      }
    }
  }

  useEffect(() => {
    clearSelectedRows()
    if (data?.count) {
      props.setTotalRowsCount?.(data.count)
      setTotalRowsInBackend(data.count)
    }
  }, [data])

  return (
    <DataTableContainer>
      <TagAssignModal
        show={showAssignContactTags}
        tagParent="contacts"
        handleClose={() => setShowAssignContactTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        tableObjAccessor="tags"
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageTags({
            values,
            tableType: 'contacts-table',
            isContactTags: true,
          })
        }}
        idAccessor="id"
      />

      <TagAssignModal
        show={showAssignCompanyTags}
        tagParent="products"
        handleClose={() => setShowAssignCompanyTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        tableObjAccessor="contact_company.taglist"
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageTags({
            values,
            tableType: 'contacts-table',
          })
        }}
        idAccessor="contact_company.id"
      />

      {showEditContactModal && (
        <EditContactModal
          show={!!showEditContactModal}
          handleClose={() => setShowEditContactModal(null)}
          onContactUpdated={props.handleContactUpdated}
          contact={showEditContactModal}
          forCompany={props.forCompany}
          initialDoorIds={props.forDoorProxyId ? [props.forDoorProxyId] : []}
        />
      )}

      {showWriteEmailForContactModal && (
        <SendEmailModal
          show={!!showWriteEmailForContactModal}
          handleClose={() => setShowWriteEmailForContactModal(null)}
          contact={showWriteEmailForContactModal}
        />
      )}

      <ContactExportModal
        tableTypeSlug="contacts-table"
        count={data?.count ?? 0}
        show={showExportModal}
        _handleClose={() => setShowExportModal(false)}
        filters={filterAndSortParams}
      />

      {bulkDelete.showModal && (
        <BulkDeleteContactsModal
          show={bulkDelete.showModal}
          onClose={bulkDelete.closeModal}
          onConfirm={bulkDelete.confirmDelete}
          isDeleting={bulkDelete.isDeleting}
          count={bulkDelete.count}
          contactIds={bulkDelete.pageVisibleIds}
          isAllRowsSelected={bulkDelete.isAllRowsSelected}
          filterParams={filterAndSortParams}
        />
      )}

      <DataTable
        key={props.tableKey}
        tableHeader={
          <S.SearchContainer>
            <S.SearchContainerFilters>
              <S.TableSearchContainer>
                <TableSearch
                  filterStore={contactsFilterStore}
                  searchPlaceholder="Search by name, company, company domain, email..."
                  tableKey={props.tableKey}
                />
              </S.TableSearchContainer>
              <S.TableButtonsContainer>
                <ContactsFilterset
                  filterIdentifier={IDENTIFIER as FilterIdentifier}
                  campaignMode={campaignMode}
                />
                <ColumnSelectorRecipient tableKey={props.tableKey} />
                {campaignMode && (
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={inSequenceOnly}
                      onChange={(e) => {
                        setInSequenceOnly(e.target.checked)
                        // Toggle changes the result set size; without a reset
                        // a user on page 3 can land on an empty page.
                        setPage(1)
                      }}
                      className="h-4 w-4 rounded border-line accent-brand focus:ring-2 focus:ring-brand"
                    />
                    <span className="text-sm font-medium text-fg-secondary">
                      In sequence
                    </span>
                  </label>
                )}
              </S.TableButtonsContainer>
            </S.SearchContainerFilters>
            <div className="flex flex-row justify-between gap-4">
              <TableButtonAction
                items={[
                  {
                    label: 'Manage Contact Labels',
                    icon: <BsTags size={20} />,
                    callback: () => setShowAssignContactTags(true),
                  },
                  {
                    label: 'Manage Company Tags',
                    icon: <BsTags size={20} />,
                    callback: () => setShowAssignCompanyTags(true),
                  },
                  {
                    label: 'Push to Salesforce',
                    icon: <SiSalesforce size={20} />,
                    hidden: !featureFlag.enablePushToSalesforce,
                    callback: () => {
                      setTimeout(() => {
                        toast.success('Successfully pushed to Salesforce')
                      }, 1000)
                    },
                  },
                  {
                    label: 'Mark as Do Not Contact',
                    icon: <Ban size={20} />,
                    callback: handleBulkMarkDoNotContact,
                    hidden: !featureFlag.enableEmailSequencing,
                  },
                  {
                    label: 'Delete Contacts',
                    icon: <XCircle size={20} />,
                    callback: () => bulkDelete.openModal(),
                  },
                ]}
                disabled={!totalSelectedRows}
                selectedRowsCount={totalSelectedRows ?? 0}
              />
            </div>
            <FilterChips
              identifier={'CONTACTS_TABLE'}
              store={contactsFilterStore}
              clearAllButton
              showActive
            />
          </S.SearchContainer>
        }
        tableKey={props.tableKey}
        loading={isFetching}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={sortableFields}
        enableRowSelection
        virtualizeRows={false}
        isPaginationEnabled={true}
        stickyLastColumn
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        selectAllText={'Select Contacts'}
        footerControls={
          !props.disableExportButton ? (
            <OverlayTrigger placement="top" overlay={tooltip}>
              <ExportButton
                variant="secondary"
                onClick={() => setShowExportModal(true)}
              >
                <BsDownload size={18} />
              </ExportButton>
            </OverlayTrigger>
          ) : undefined
        }
      />
    </DataTableContainer>
  )
}

ContactsTableComponent.displayName = 'ContactsTable'

export function ContactsTable(
  props: ContactsTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, campaignId, ...rest } = props

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      defaultColumnVisibility={defaultColumnVisibility}
      preferredGroupOrder={
        campaignId
          ? ['Sequencing', 'Contact Information', 'Activity', 'Account Details']
          : ['Contact Information', 'Sequencing', 'Activity', 'Account Details']
      }
    >
      <DataTableProvider tableKey={tableKey}>
        <ContactsTableComponent
          tableKey={tableKey}
          campaignId={campaignId}
          {...rest}
        />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}

const ExportButton = styled(FbButton)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 14px;

  gap: 8px;

  height: 44px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`
